/**
 * Tenant Entitlements Hooks
 *
 * React Query hooks for the per-tenant entitlement state managed by the
 * platform-admin-api Tenant Entitlements surface (Sprint C15-C WP D-3):
 *
 *   GET  /api/admin/tenants/{tenantId}/entitlements -> TenantEntitlements
 *   PUT  /api/admin/tenants/{tenantId}/entitlements -> TenantEntitlements
 *
 * Both operations require platform-admin role and 403 for FI operators.
 *
 * On a successful update, this module invalidates BOTH the per-tenant
 * entitlements query AND the tenants list query so the Tenants list grid
 * re-renders the entitlement badges immediately.
 *
 * Types are derived from platform-admin.openapi.yaml via
 * @aaimplatform/institutional-types.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { platformAdminClient } from '../api/typed-clients';
import { apiErrorMessage } from '../api/error-message';

export type TenantEntitlements = components['schemas']['TenantEntitlements'];
export type TenantEntitlementsUpdate =
  components['schemas']['TenantEntitlementsUpdate'];

const ENTITLEMENTS_QUERY_KEY = 'tenant-entitlements';
const TENANTS_QUERY_KEY = 'tenants';

function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as
      | { message?: string; code?: string }
      | undefined;
    throw new Error(
      apiErrorMessage(errorBody) ?? `HTTP ${status}: ${statusText}`,
    );
  }
  return result.data;
}

/**
 * Fetch the entitlement state for a single tenant.
 *
 * Disabled when tenantId is empty so the page can render skeletons
 * before the route param resolves.
 */
export function useTenantEntitlements(tenantId: string) {
  return useQuery<TenantEntitlements, Error>({
    queryKey: [ENTITLEMENTS_QUERY_KEY, tenantId],
    queryFn: async () => {
      const result = await platformAdminClient.GET(
        '/tenants/{tenantId}/entitlements',
        {
          params: { path: { tenantId } },
        },
      );
      return unwrap(result);
    },
    enabled: !!tenantId,
  });
}

/**
 * Update one or more entitlement flags for a tenant. The API accepts a
 * partial update (each Boolean is independently optional); omitted
 * fields are left unchanged. Each toggle that mutates state writes one
 * audit-log row server-side.
 *
 * On success: invalidate the per-tenant entitlements query AND the
 * tenants list so the entitlement column on the list page refreshes.
 */
export function useUpdateTenantEntitlements(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation<TenantEntitlements, Error, TenantEntitlementsUpdate>({
    mutationFn: async (body: TenantEntitlementsUpdate) => {
      const result = await platformAdminClient.PUT(
        '/tenants/{tenantId}/entitlements',
        {
          params: { path: { tenantId } },
          body,
        },
      );
      return unwrap(result);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [ENTITLEMENTS_QUERY_KEY, tenantId],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [ENTITLEMENTS_QUERY_KEY, tenantId],
      });
      queryClient.invalidateQueries({ queryKey: [TENANTS_QUERY_KEY] });
    },
  });
}
